(function () {
  'use strict';

  var STORAGE_ZIP = 'wizzy-last-zip';
  var STORAGE_RADIUS = 'wizzy-last-location-radius';
  var STORAGE_SOURCES = 'wizzy_product_inventory_sources_ids';
  var STORAGE_EZ = 'ez-location-preference';
  var STORAGE_STORES = 'ez-location-stores';
  var GEOCODE_API =
    'https://nv0lfbt1zk.execute-api.us-east-1.amazonaws.com/geocode?zip={zip}&radius={radius}';
  var EARTH_RADIUS_MI = 3958.8;
  var DEFAULT_RADIUS = 25;
  var ZIP_DIGITS = 5;
  function normalizeRadius(value) {
    var n = parseInt(value, 10);
    if (isNaN(n)) return DEFAULT_RADIUS;
    return Math.min(100, Math.max(1, n));
  }
  var INPUT_DEBOUNCE_MS = 450;
  var INPUT_THROTTLE_MS = 800;
  var KNOWN_USER_ERRORS = [
    'Could not find that location',
    'Could not resolve your location',
    'Could not resolve location',
    'Google Maps failed to load',
    'Maps API key is not configured.',
    'Please enter a valid 5-digit zip code.',
    'Location is not supported in this browser.',
    'Location permission denied.',
    'Could not load stores for this location.',
  ];

  function setTextContent(el, text) {
    if (el) el.textContent = text == null ? '' : text;
  }

  function isTechnicalErrorMessage(message) {
    if (!message || typeof message !== 'string') return true;
    return (
      /cannot read propert/i.test(message) ||
      /cannot set propert/i.test(message) ||
      /is not a function/i.test(message) ||
      /is not a constructor/i.test(message) ||
      /unexpected token/i.test(message) ||
      /\[object (object|event)\]/i.test(message)
    );
  }

  function userFacingError(err, fallback) {
    fallback = fallback || 'Something went wrong. Please try again.';
    if (!err) return fallback;
    var message = typeof err === 'string' ? err : err.message || '';
    if (isTechnicalErrorMessage(message)) return fallback;
    if (KNOWN_USER_ERRORS.indexOf(message) >= 0) return message;
    return fallback;
  }

  function isPartialZipInput(value) {
    var trimmed = (value || '').trim();
    return /^\d{1,4}$/.test(trimmed);
  }

  function isCompleteZipInput(value) {
    var trimmed = (value || '').trim();
    return /^\d{5}(-\d{4})?$/.test(trimmed);
  }

  function hasCompleteZip(value) {
    var zip = extractZipFromQuery(value);
    return !!(zip && zip.length === ZIP_DIGITS);
  }

  function toRad(deg) {
    return (deg * Math.PI) / 180;
  }

  function isValidCoord(lat, lng) {
    if (lat == null || lng == null || isNaN(lat) || isNaN(lng)) return false;
    if (lat === 0 && lng === 0) return false;
    return lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180;
  }

  function sanitizeCoord(value) {
    if (value == null || value === '') return null;
    var n = parseFloat(value);
    return isNaN(n) ? null : n;
  }

  function milesBetween(lat1, lng1, lat2, lng2) {
    var dLat = toRad(lat2 - lat1);
    var dLng = toRad(lng2 - lng1);
    var a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
    var c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return EARTH_RADIUS_MI * c;
  }

  function formatDistance(miles) {
    if (miles < 10) return miles.toFixed(1) + ' mi';
    return Math.round(miles) + ' mi';
  }

  function isReasonableDistance(miles, radius) {
    if (miles == null || isNaN(miles) || miles < 0) return false;
    return miles <= radius + 15;
  }

  function extractZipFromQuery(query) {
    var trimmed = (query || '').trim();
    if (/^\d{5}(-\d{4})?$/.test(trimmed)) return trimmed.slice(0, 5);
    var match = trimmed.match(/\b(\d{5})\b/);
    return match ? match[1] : '';
  }

  function normalizeStore(raw, options) {
    options = options || {};
    if (!raw || !raw.id) return null;
    var lat =
      raw.latitude != null
        ? parseFloat(raw.latitude)
        : raw.lat != null
          ? parseFloat(raw.lat)
          : NaN;
    var lng =
      raw.longitude != null
        ? parseFloat(raw.longitude)
        : raw.lng != null
          ? parseFloat(raw.lng)
          : NaN;
    if (isNaN(lat) || isNaN(lng)) {
      if (!options.allowMissingCoords) return null;
      lat = NaN;
      lng = NaN;
    }
    var street = raw.address || '';
    var city = raw.city || '';
    var state = raw.state || '';
    var line = street;
    if (city || state) {
      line = [street, [city, state].filter(Boolean).join(', ')].filter(Boolean).join(', ');
    }
    var hours = (raw.hours || '').trim();
    return {
      id: String(raw.id),
      name: raw.name2 || raw.name || raw.brand || 'Store',
      address: line,
      lat: lat,
      lng: lng,
      zip: raw.zip || '',
      hoursToday: hours || 'See store for hours',
      isOpenNow: true,
      itemCount: 0,
    };
  }

  function mapApiStoreForDisplay(raw, lat, lng, radius) {
    var store = normalizeStore(raw, { allowMissingCoords: true });
    if (!store) return null;
    if (isValidCoord(lat, lng) && isValidCoord(store.lat, store.lng)) {
      store.distanceMi = milesBetween(lat, lng, store.lat, store.lng);
    } else {
      var apiDistance = null;
      if (raw.distance != null) apiDistance = parseFloat(raw.distance);
      else if (raw.distance_mi != null) apiDistance = parseFloat(raw.distance_mi);
      else if (raw.distanceMi != null) apiDistance = parseFloat(raw.distanceMi);
      if (isReasonableDistance(apiDistance, radius)) {
        store.distanceMi = apiDistance;
      }
    }
    return store;
  }

  if (!window.EzLocationStores || !window.EzLocationStores.register) {
    console.warn('[ez-location-sheet] EzLocationStores registry missing');
  }

  function loadStoresFromDom() {
    if (!window.EzLocationStores || !window.EzLocationStores.register) return;
    document.querySelectorAll('[id^="ez-find-location-data-"], [id^="ez-location-stores-data-"]').forEach(function (el) {
      try {
        var stores = JSON.parse(el.textContent);
        window.EzLocationStores.register(stores, el.id);
      } catch (e) {}
    });
  }

  function getStores() {
    loadStoresFromDom();
    var list = window.EzLocationStores.getAll();
    if (list.length) return list.map(normalizeStore).filter(Boolean);
    try {
      var cached = JSON.parse(sessionStorage.getItem(STORAGE_STORES) || '[]');
      if (cached.length) return cached.map(normalizeStore).filter(Boolean);
    } catch (e) {}
    if (window.EZ_FIND_LOCATION_DATA && window.EZ_FIND_LOCATION_DATA.length) {
      return window.EZ_FIND_LOCATION_DATA.map(normalizeStore).filter(Boolean);
    }
    return [];
  }

  function getFacetItemCounts() {
    var counts = {};
    document.querySelectorAll('[data-key="product_inventory_sources_ids"] [data-term]').forEach(function (el) {
      var id = el.getAttribute('data-term');
      var countEl = el.querySelector('.wizzy-facet-list-item-count');
      if (!id || !countEl) return;
      var n = parseInt(countEl.textContent.replace(/[()]/g, ''), 10);
      counts[id] = isNaN(n) ? 0 : n;
    });
    return counts;
  }

  function enrichItemCounts(stores) {
    var counts = getFacetItemCounts();
    var hasFacets = Object.keys(counts).length > 0;
    return stores.map(function (s) {
      var copy = Object.assign({}, s);
      if (hasFacets && counts.hasOwnProperty(s.id)) {
        copy.itemCount = counts[s.id];
      }
      return copy;
    });
  }

  function makeGeocodeApiUrl(zip, radius) {
    return GEOCODE_API.replace('{zip}', encodeURIComponent(zip)).replace(
      '{radius}',
      encodeURIComponent(radius)
    );
  }

  function fetchGeocodeStores(zip, radius) {
    if (!zip || zip.length !== 5) return Promise.resolve([]);
    var url = makeGeocodeApiUrl(zip, radius);
    var headers = { Accept: 'application/json' };
    if (window.wizzyConfig && window.wizzyConfig.credentials) {
      headers['x-store-id'] = window.wizzyConfig.credentials.storeId;
      headers['x-store-key'] = window.wizzyConfig.credentials.apiKey;
    }
    return fetch(url, { headers: headers })
      .then(function (res) {
        return res.json();
      })
      .then(function (data) {
        if (data && Array.isArray(data.result)) return data.result;
        return [];
      })
      .catch(function () {
        return [];
      });
  }

  function readStoredStoreIds() {
    try {
      var raw = localStorage.getItem(STORAGE_SOURCES);
      var ids = raw ? JSON.parse(raw) : [];
      return Array.isArray(ids) ? ids.map(String) : [];
    } catch (e) {
      return [];
    }
  }

  function queryKeyName(part) {
    if (!part) return '';
    var key = part.split('=')[0] || '';
    try {
      return decodeURIComponent(key.replace(/\+/g, ' '));
    } catch (e) {
      return key;
    }
  }

  function cloneJson(value) {
    try {
      return JSON.parse(JSON.stringify(value));
    } catch (e) {
      return null;
    }
  }

  function urlHasUserFacets(href) {
    try {
      var url = new URL(href, window.location.origin);
      var keys = [];
      url.searchParams.forEach(function (_value, key) {
        keys.push(key);
      });
      for (var i = 0; i < keys.length; i++) {
        var key = keys[i];
        if (key === 'q' || key === 'searchedKey' || key === 'categories[]') return true;
        if (key.indexOf('product_') === 0 && key.indexOf('product_inventory_sources') !== 0) {
          // Ignore hard-coded sort tag params from the bare rebuild.
          if (key.indexOf('product_value_tags_image_based_sort') === 0) continue;
          return true;
        }
      }
    } catch (e) {}
    return false;
  }

  function isBareHomeUrl(href) {
    try {
      var url = new URL(href, window.location.origin);
      var path = url.pathname || '/';
      if (path !== '/' && path !== '') return false;
      return !urlHasUserFacets(href);
    } catch (e) {
      return false;
    }
  }

  function inventoryIdsFromHref(href) {
    try {
      var url = new URL(href, window.location.origin);
      return url.searchParams.getAll('product_inventory_sources_ids[]').map(String);
    } catch (e) {
      return [];
    }
  }

  function getActiveSearchPath() {
    var common = (window.wizzyConfig && window.wizzyConfig.common) || {};
    if (common.isOnCategoryPage && common.categoryEndpoint) {
      return common.categoryEndpoint.charAt(0) === '/'
        ? common.categoryEndpoint
        : '/' + common.categoryEndpoint;
    }
    var endpoint =
      window.wizzyConfig &&
      window.wizzyConfig.search &&
      window.wizzyConfig.search.configs &&
      window.wizzyConfig.search.configs.general &&
      window.wizzyConfig.search.configs.general.searchEndpoint;
    if (endpoint) {
      return endpoint.charAt(0) === '/' ? endpoint : '/' + endpoint;
    }
    var path = window.location.pathname || '/';
    return path;
  }

  // Keep the exact current path + every non-inventory facet, without re-encoding
  // the rest of the query (URLSearchParams would rewrite [] and retrigger filters).
  function buildLocationFilterUrlFromAddressBar(storeIds) {
    storeIds = Array.isArray(storeIds) ? storeIds.map(String) : [];
    var search = window.location.search || '';
    var parts = search.charAt(0) === '?' ? search.slice(1).split('&') : search.split('&');
    if (parts.length === 1 && parts[0] === '') parts = [];

    var kept = [];
    for (var i = 0; i < parts.length; i++) {
      var key = queryKeyName(parts[i]);
      if (key === 'product_inventory_sources_ids[]' || key === 'product_inventory_sources_ids') {
        continue;
      }
      if (key === 'page') {
        kept.push('page=1');
        continue;
      }
      kept.push(parts[i]);
    }

    for (var j = 0; j < storeIds.length; j++) {
      kept.push('product_inventory_sources_ids[]=' + encodeURIComponent(storeIds[j]));
    }

    var qs = kept.join('&');
    return (
      window.location.origin +
      window.location.pathname +
      (qs ? '?' + qs : '') +
      (window.location.hash || '')
    );
  }

  // When the address bar is already bare "/" (common after earlier zip URL bugs),
  // rebuild from Wizzy's in-memory filters so clear/apply cannot collapse to homepage.
  function buildLocationFilterUrlFromWizzyState(storeIds) {
    storeIds = Array.isArray(storeIds) ? storeIds.map(String) : [];
    if (!window.wizzyConfig || !window.wizzyCustomUrlUtils || typeof window.wizzyCustomUrlUtils.encodeFilters !== 'function') {
      return null;
    }
    var pageStore = window.wizzyConfig.pageStore;
    if (!pageStore || !pageStore.searchedResponse || !pageStore.searchedResponse.filters) {
      return null;
    }

    var cleaned = cloneJson(pageStore.searchedResponse.filters);
    if (!cleaned) return null;

    try {
      if (window.wizzyGeoCodeFilter && typeof window.wizzyGeoCodeFilter.getCleanedSearchData === 'function') {
        cleaned = window.wizzyGeoCodeFilter.getCleanedSearchData(cleaned);
      }
    } catch (e) {}

    if (cleaned && cleaned.attributes) {
      delete cleaned.attributes.product_inventory_sources_ids;
      delete cleaned.attributes.product_city_tags;
    }

    var q = pageStore.searchSubmitValue || pageStore.searchInputValue || cleaned.q || '';
    if (q && q.length) cleaned.q = q;

    var qs = window.wizzyCustomUrlUtils.encodeFilters(cleaned);
    if (!qs) {
      if (!q) return null;
      qs = 'q=' + encodeURIComponent(q);
    }

    for (var i = 0; i < storeIds.length; i++) {
      qs += (qs ? '&' : '') + 'product_inventory_sources_ids[]=' + encodeURIComponent(storeIds[i]);
    }

    return window.location.origin + getActiveSearchPath() + '?' + qs + (window.location.hash || '');
  }

  function buildBestLocationFilterUrl(storeIds) {
    var fromAddress = buildLocationFilterUrlFromAddressBar(storeIds);
    if (urlHasUserFacets(fromAddress) && !isBareHomeUrl(fromAddress)) {
      return fromAddress;
    }
    return buildLocationFilterUrlFromWizzyState(storeIds) || fromAddress;
  }

  function refreshWizzyFiltersInPlace() {
    try {
      if (window.jQuery && typeof window.jQuery.fn.refreshFilters === 'function') {
        window.jQuery.fn.refreshFilters(true);
      }
    } catch (e) {}
  }

  // Blocks search-response → executeFilterApi from re-entering while we just applied.
  var locationFilterQuietUntil = 0;

  function executeWizzyLocationFilter(storeIds, forceRefresh) {
    if (document.body.classList.contains('wizzy-ignore-zipcode-info')) return;
    if (Date.now() < locationFilterQuietUntil) return;

    if (!window.wizzyConfig) return;
    var pageStore = window.wizzyConfig.pageStore;
    if (!pageStore || !pageStore.searchedResponse || !pageStore.searchedResponse.filters) {
      return;
    }

    storeIds = Array.isArray(storeIds) ? storeIds.map(String) : [];
    var nextUrl = buildBestLocationFilterUrl(storeIds);

    // Never navigate an active search/collection view to a bare homepage URL.
    if (isBareHomeUrl(nextUrl)) {
      var q = pageStore.searchSubmitValue || pageStore.searchInputValue || '';
      if (q && String(q).length) {
        nextUrl =
          window.location.origin +
          getActiveSearchPath() +
          '?q=' +
          encodeURIComponent(q);
        for (var si = 0; si < storeIds.length; si++) {
          nextUrl += '&product_inventory_sources_ids[]=' + encodeURIComponent(storeIds[si]);
        }
      } else {
        locationFilterQuietUntil = Date.now() + 1500;
        refreshWizzyFiltersInPlace();
        return;
      }
    }

    if (nextUrl === window.location.href) {
      if (!forceRefresh) return;
      locationFilterQuietUntil = Date.now() + 1500;
      refreshWizzyFiltersInPlace();
      return;
    }

    locationFilterQuietUntil = Date.now() + 1500;
    if (typeof window.executeWizzyFiltersFromURL === 'function') {
      window.executeWizzyFiltersFromURL(nextUrl);
    } else {
      try {
        history.replaceState(history.state, document.title, nextUrl);
      } catch (e) {}
      refreshWizzyFiltersInPlace();
    }
  }

  // Last line of defense: refuse bare homepage-style filter URLs while search is active.
  function installSafeExecuteWizzyFiltersFromURL() {
    function patch() {
      if (typeof window.executeWizzyFiltersFromURL !== 'function') return false;
      if (window.executeWizzyFiltersFromURL.__ezSafe) return true;

      var original = window.executeWizzyFiltersFromURL;
      window.executeWizzyFiltersFromURL = function (url) {
        try {
          var activeSearch = !!(
            window.wizzyConfig &&
            window.wizzyConfig.pageStore &&
            window.wizzyConfig.pageStore.searchedResponse
          );
          var ids = inventoryIdsFromHref(url);
          if (activeSearch && isBareHomeUrl(url)) {
            url = buildBestLocationFilterUrl(ids);
          } else if (urlHasUserFacets(window.location.href) && !urlHasUserFacets(url)) {
            url = buildBestLocationFilterUrl(ids);
          }
          if (activeSearch && isBareHomeUrl(url)) {
            refreshWizzyFiltersInPlace();
            return;
          }
        } catch (e) {}
        return original.call(this, url);
      };
      window.executeWizzyFiltersFromURL.__ezSafe = true;
      return true;
    }

    patch();
    var tries = 0;
    var timer = setInterval(function () {
      if (patch() || ++tries > 40) clearInterval(timer);
    }, 250);
  }

  // wizzyCustom.js calls executeFilterApi after every search response; the stock
  // implementation rebuilds origin+pathname+defaults and wipes the search URL.
  // Re-apply for a few seconds in case wizzyEzGeocode assigns the stock fn later.
  function installSafeExecuteFilterApi() {
    function patch() {
      if (!window.wizzyGeoCodeFilter || typeof window.wizzyGeoCodeFilter.executeFilterApi !== 'function') {
        return false;
      }
      if (
        window.wizzyGeoCodeFilter.__ezSafeExecuteFilterApi &&
        window.wizzyGeoCodeFilter.executeFilterApi &&
        window.wizzyGeoCodeFilter.executeFilterApi.__ezSafe
      ) {
        return true;
      }

      var safeFn = function () {
        executeWizzyLocationFilter(readStoredStoreIds(), false);
      };
      safeFn.__ezSafe = true;
      window.wizzyGeoCodeFilter.executeFilterApi = safeFn;
      window.wizzyGeoCodeFilter.__ezSafeExecuteFilterApi = true;
      return true;
    }

    patch();
    var tries = 0;
    var timer = setInterval(function () {
      patch();
      if (++tries > 40) clearInterval(timer);
    }, 250);
  }

  function loadPreference() {
    var pref = null;
    try {
      var raw = localStorage.getItem(STORAGE_EZ);
      if (raw) pref = JSON.parse(raw);
    } catch (e) {}
    var storedRadius = parseInt(localStorage.getItem(STORAGE_RADIUS), 10);
    if (pref) {
      if ((pref.radius == null || pref.radius === '') && !isNaN(storedRadius)) {
        pref.radius = storedRadius;
      }
      return pref;
    }
    var zip = localStorage.getItem(STORAGE_ZIP);
    if (!zip) return null;
    return {
      query: zip,
      zip: zip,
      radius: isNaN(storedRadius) ? DEFAULT_RADIUS : storedRadius,
    };
  }

  function savePreference(pref) {
    try {
      localStorage.setItem(STORAGE_EZ, JSON.stringify(pref));
    } catch (e) {}
    if (pref.zip) localStorage.setItem(STORAGE_ZIP, pref.zip);
    localStorage.setItem(STORAGE_RADIUS, String(pref.radius));
  }

  function clearStoredLocation() {
    try {
      localStorage.removeItem(STORAGE_EZ);
      localStorage.removeItem(STORAGE_ZIP);
      localStorage.removeItem(STORAGE_SOURCES);
    } catch (e) {}
  }

  function clearWizzyZipInputs() {
    document.querySelectorAll('#wizzy-zipcode-input-box, [name="wizzy-zip"]').forEach(function (el) {
      el.value = '';
    });
  }

  function syncWizzyAfterClear(radius) {
    if (window.wizzyGeoCodeFilter) {
      try {
        if (typeof window.wizzyGeoCodeFilter.setCurrentZip === 'function') {
          window.wizzyGeoCodeFilter.setCurrentZip('', true);
        }
        if (typeof window.wizzyGeoCodeFilter.resetLocalLocations === 'function') {
          window.wizzyGeoCodeFilter.resetLocalLocations();
        } else if (typeof window.wizzyGeoCodeFilter.resetLocalCollection === 'function') {
          window.wizzyGeoCodeFilter.resetLocalCollection();
        }
      } catch (e) {}
    }
    clearWizzyZipInputs();
    try {
      // setCurrentZip('', ...) writes wizzy-last-zip=""; remove it so we don't keep
      // a falsy zip key around for the popstate reload logic.
      localStorage.removeItem(STORAGE_ZIP);
      localStorage.removeItem('wizzy-previous-applied-zipcode');
      localStorage.setItem(STORAGE_SOURCES, JSON.stringify([]));
    } catch (e) {}
    document.body.classList.remove('wizzy-have-pincode');
    document.body.classList.add('wizzy-dont-have-pincode');
    document.dispatchEvent(
      new CustomEvent('wizzy:location-updated', {
        detail: { zip: '', radius: radius, storeIds: [] },
      })
    );
    executeWizzyLocationFilter([], true);
  }

  function isGoogleMapsReady() {
    return !!(window.google && window.google.maps && window.google.maps.Geocoder);
  }

  function waitForGoogleMapsScript(reject) {
    return new Promise(function (resolve) {
      if (isGoogleMapsReady()) {
        resolve();
        return;
      }
      var attempts = 0;
      var maxAttempts = 200;
      function poll() {
        if (isGoogleMapsReady()) {
          resolve();
          return;
        }
        if (++attempts >= maxAttempts) {
          if (reject) reject(new Error('Google Maps failed to load'));
          return;
        }
        setTimeout(poll, 50);
      }
      var existing = document.querySelector('script[src*="maps.googleapis.com/maps/api/js"]');
      if (existing) {
        existing.addEventListener('load', poll);
        existing.addEventListener('error', function () {
          if (reject) reject(new Error('Google Maps failed to load'));
        });
      }
      poll();
    });
  }

  function loadGoogleMaps(apiKey) {
    return new Promise(function (resolve, reject) {
      if (isGoogleMapsReady()) {
        resolve();
        return;
      }
      if (document.querySelector('script[src*="maps.googleapis.com/maps/api/js"]')) {
        waitForGoogleMapsScript(reject).then(resolve).catch(reject);
        return;
      }
      var cb = '__ezLocationMapsInit_' + Date.now();
      window[cb] = function () {
        delete window[cb];
        resolve();
      };
      var s = document.createElement('script');
      s.src =
        'https://maps.googleapis.com/maps/api/js?key=' +
        encodeURIComponent(apiKey) +
        '&libraries=places&callback=' +
        cb;
      s.async = true;
      s.defer = true;
      s.setAttribute('data-ez-maps-loader', '1');
      s.onerror = reject;
      document.head.appendChild(s);
    });
  }

  function geocodeQuery(query, apiKey) {
    return loadGoogleMaps(apiKey).then(function () {
      return new Promise(function (resolve, reject) {
        var geocoder = new google.maps.Geocoder();
        geocoder.geocode({ address: query, componentRestrictions: { country: 'US' } }, function (results, status) {
          if (status !== 'OK' || !results || !results[0]) {
            reject(new Error('Could not find that location'));
            return;
          }
          var loc = results[0].geometry.location;
          var label = results[0].formatted_address || query;
          var parts = label.split(',');
          var cityLabel = parts.length >= 2 ? parts.slice(0, 2).join(',').trim() : label;
          var zip = '';
          (results[0].address_components || []).forEach(function (c) {
            if (c.types.indexOf('postal_code') >= 0) zip = c.short_name;
          });
          if (!zip) zip = extractZipFromQuery(query);
          resolve({
            lat: loc.lat(),
            lng: loc.lng(),
            label: cityLabel,
            query: query.trim(),
            zip: zip,
          });
        });
      });
    });
  }

  function findZipFromCoords(lat, lng, apiKey) {
    return loadGoogleMaps(apiKey).then(function () {
      return new Promise(function (resolve, reject) {
        var geocoder = new google.maps.Geocoder();
        geocoder.geocode({ location: { lat: lat, lng: lng } }, function (results, status) {
          if (status !== 'OK' || !results || !results[0]) {
            reject(new Error('Could not resolve your location'));
            return;
          }
          var zip = '';
          (results[0].address_components || []).forEach(function (c) {
            if (c.types.indexOf('postal_code') >= 0) zip = c.short_name;
          });
          var label = results[0].formatted_address || '';
          var parts = label.split(',');
          var cityLabel = parts.length >= 2 ? parts.slice(0, 2).join(',').trim() : label;
          resolve({
            lat: lat,
            lng: lng,
            zip: zip,
            label: cityLabel,
            query: zip || label,
          });
        });
      });
    });
  }

  function init() {
    var root = document.getElementById('ez-location-sheet-root');
    if (!root) return;

    var apiKey = root.getAttribute('data-maps-key') || '';
    var panel = root.querySelector('.ez-location-sheet__panel');
    var queryInput = root.querySelector('[data-ez-location-query]');
    var radiusInput = root.querySelector('[data-ez-location-radius]');
    var radiusLabel = root.querySelector('[data-ez-location-radius-label]');
    var statusEl = root.querySelector('[data-ez-location-status]');
    var storesList = root.querySelector('[data-ez-location-stores-list]');
    var storesEmpty = root.querySelector('[data-ez-location-stores-empty]');
    var storeCountEl = root.querySelector('[data-ez-location-store-count]');
    var itemCountEl = root.querySelector('[data-ez-location-item-count]');
    var applyCountEl = root.querySelector('[data-ez-location-apply-count]');
    var applyBtn = root.querySelector('[data-ez-location-apply]');
    var geoBtn = root.querySelector('[data-ez-location-geolocate]');
    var clearBtn = root.querySelector('[data-ez-location-clear]');

    var state = {
      query: '',
      zip: '',
      geocodedZip: '',
      locationLabel: '',
      lat: null,
      lng: null,
      radius: DEFAULT_RADIUS,
      apiStores: [],
      matched: [],
      geocodeTimer: null,
      storesFetchTimer: null,
      inputDebounceTimer: null,
      throttleTimer: null,
      lastApiCallAt: 0,
      storesFetchRequestId: 0,
      isGeocoding: false,
      isFetchingStores: false,
    };

    loadStoresFromDom();

    var pref = loadPreference();
    if (pref) {
      state.query = pref.query || pref.zip || '';
      state.zip = pref.zip || '';
      state.locationLabel = pref.locationLabel || '';
      state.lat = sanitizeCoord(pref.lat);
      state.lng = sanitizeCoord(pref.lng);
      if (!isValidCoord(state.lat, state.lng)) {
        state.lat = null;
        state.lng = null;
      } else if (
        pref.zip &&
        pref.zip.length === ZIP_DIGITS &&
        isCompleteZipInput(pref.query || pref.zip)
      ) {
        state.geocodedZip = pref.zip;
      }
      state.radius = normalizeRadius(pref.radius || DEFAULT_RADIUS);
      queryInput.value = state.query;
    } else {
      state.radius = DEFAULT_RADIUS;
    }

    function syncRadiusControls() {
      state.radius = normalizeRadius(state.radius);
      if (radiusInput) radiusInput.value = state.radius;
      setTextContent(radiusLabel, state.radius + ' mi');
    }

    syncRadiusControls();

    function getDistanceOrigin() {
      if (!isValidCoord(state.lat, state.lng)) return null;
      var zip = state.zip || extractZipFromQuery(state.query);
      if (zip && zip.length === ZIP_DIGITS && isCompleteZipInput(state.query || zip)) {
        if (state.geocodedZip !== zip) return null;
      }
      return { lat: state.lat, lng: state.lng };
    }

    function isDistancePending() {
      if (state.isGeocoding) return true;
      var zip = state.zip || extractZipFromQuery(state.query);
      if (!zip || zip.length !== ZIP_DIGITS) return false;
      if (!isCompleteZipInput(state.query || zip)) return false;
      return !getDistanceOrigin();
    }

    function sortStoresByDistance(stores) {
      return stores.slice().sort(function (a, b) {
        var da = a.distanceMi != null && !isNaN(a.distanceMi) ? a.distanceMi : Infinity;
        var db = b.distanceMi != null && !isNaN(b.distanceMi) ? b.distanceMi : Infinity;
        return da - db;
      });
    }

    function setOpen(open) {
      root.classList.toggle('is-open', open);
      root.hidden = !open;
      panel.setAttribute('aria-hidden', open ? 'false' : 'true');
      document.body.classList.toggle('ez-location-sheet-open', open);
      if (open) {
        updateUi();
        setTimeout(function () {
          queryInput.focus();
        }, 280);
      }
    }

    function showStatus(text, isError) {
      if (!statusEl) return;
      if (!text) {
        statusEl.hidden = true;
        statusEl.textContent = '';
        statusEl.classList.remove('is-error');
        return;
      }
      statusEl.hidden = false;
      statusEl.textContent = text;
      statusEl.classList.toggle('is-error', !!isError);
    }

    function safeUpdateUi() {
      try {
        updateUi();
      } catch (err) {
        console.error('[ez-location-sheet] updateUi failed', err);
        showStatus(
          'We couldn\u2019t load stores for that location. Try a zip code (78704) or city and state (Austin, TX).',
          true
        );
      }
    }

    function filterStoresFromLocal() {
      var all = enrichItemCounts(getStores());
      var origin = getDistanceOrigin();
      if (!origin || !all.length) {
        return [];
      }
      return all
        .map(function (s) {
          return Object.assign({}, s, {
            distanceMi: milesBetween(origin.lat, origin.lng, s.lat, s.lng),
          });
        })
        .filter(function (s) {
          return s.distanceMi <= state.radius;
        })
        .sort(function (a, b) {
          return a.distanceMi - b.distanceMi;
        });
    }

    function filterStores() {
      var zip = state.zip || extractZipFromQuery(state.query);
      if (state.apiStores.length && zip && zip.length === 5) {
        var origin = getDistanceOrigin();
        var originLat = origin ? origin.lat : null;
        var originLng = origin ? origin.lng : null;
        state.matched = enrichItemCounts(
          sortStoresByDistance(
            state.apiStores
              .map(function (store) {
                return mapApiStoreForDisplay(store, originLat, originLng, state.radius);
              })
              .filter(Boolean)
              .filter(function (s) {
                if (s.distanceMi == null || isNaN(s.distanceMi)) return true;
                return s.distanceMi <= state.radius;
              })
          )
        );
        return;
      }
      state.matched = filterStoresFromLocal();
    }

    function clearPendingInputTimers() {
      clearTimeout(state.geocodeTimer);
      clearTimeout(state.storesFetchTimer);
      clearTimeout(state.inputDebounceTimer);
      clearTimeout(state.throttleTimer);
    }

    function runThrottledApiCall(fn) {
      var now = Date.now();
      var elapsed = now - state.lastApiCallAt;
      if (elapsed < INPUT_THROTTLE_MS) {
        clearTimeout(state.throttleTimer);
        state.throttleTimer = setTimeout(function () {
          state.lastApiCallAt = Date.now();
          fn();
        }, INPUT_THROTTLE_MS - elapsed);
        return;
      }
      state.lastApiCallAt = now;
      fn();
    }

    function clearStorePreview() {
      state.apiStores = [];
      state.matched = [];
      filterStores();
    }

    function fetchStoresForPreview() {
      var zip = state.zip || extractZipFromQuery(queryInput.value.trim());
      if (!zip || zip.length !== ZIP_DIGITS) {
        clearStorePreview();
        updateUi();
        return;
      }

      state.zip = zip;
      var requestId = ++state.storesFetchRequestId;
      state.isFetchingStores = true;
      updateUi();

      fetchGeocodeStores(zip, state.radius)
        .then(function (stores) {
          if (requestId !== state.storesFetchRequestId) return;
          state.isFetchingStores = false;
          state.apiStores = stores || [];
          filterStores();
          updateUi();
        })
        .catch(function () {
          if (requestId !== state.storesFetchRequestId) return;
          state.isFetchingStores = false;
          state.apiStores = [];
          filterStores();
          updateUi();
        });
    }

    function scheduleStoresFetch(immediate) {
      var zip = state.zip || extractZipFromQuery(queryInput.value.trim());
      if (!zip || zip.length !== ZIP_DIGITS) return;
      clearTimeout(state.storesFetchTimer);
      var run = function () {
        runThrottledApiCall(fetchStoresForPreview);
      };
      if (immediate) {
        run();
      } else {
        state.storesFetchTimer = setTimeout(run, INPUT_DEBOUNCE_MS);
      }
    }

    function totalItems() {
      return state.matched.reduce(function (sum, s) {
        return sum + (s.itemCount || 0);
      }, 0);
    }

    function renderStoreList() {
      if (!storesList) return;
      storesList.innerHTML = '';
      var distancePending = isDistancePending();
      var show = state.matched.length && !state.isFetchingStores && !distancePending;
      if (storesEmpty) storesEmpty.hidden = !!show;
      if (distancePending && (state.isFetchingStores || state.matched.length || state.isGeocoding)) {
        if (storesEmpty) {
          storesEmpty.hidden = false;
          setTextContent(
            storesEmpty,
            state.isGeocoding ? 'Calculating distances…' : 'Finding stores near you…'
          );
        }
        return;
      }
      if (!show) {
        setTextContent(storesEmpty, 'Enter a location above to see stores within your radius.');
        return;
      }

      state.matched.forEach(function (s) {
        var li = document.createElement('li');
        li.className = 'ez-location-sheet__store-row' + (s.isOpenNow ? '' : ' is-closed');

        var main = document.createElement('div');
        main.className = 'ez-location-sheet__store-main';

        var nameEl = document.createElement('div');
        nameEl.className = 'ez-location-sheet__store-name';
        nameEl.textContent = s.name || 'Store';

        var addressEl = document.createElement('div');
        addressEl.className = 'ez-location-sheet__store-address';
        addressEl.textContent = s.address || '';

        var hoursEl = document.createElement('div');
        hoursEl.className = 'ez-location-sheet__store-hours';
        hoursEl.textContent = s.hoursToday || 'See store for hours';

        main.appendChild(nameEl);
        main.appendChild(addressEl);
        main.appendChild(hoursEl);

        var meta = document.createElement('div');
        meta.className = 'ez-location-sheet__store-meta';

        var distanceEl = document.createElement('div');
        distanceEl.className = 'ez-location-sheet__store-distance';
        distanceEl.textContent =
          s.distanceMi != null && !isNaN(s.distanceMi) ? formatDistance(s.distanceMi) : '—';

        var itemsEl = document.createElement('div');
        // itemsEl.className = 'ez-location-sheet__store-items';
        // itemsEl.textContent =
        //   s.itemCount > 0 ? s.itemCount.toLocaleString() + ' items' : '— items';

        meta.appendChild(distanceEl);
        meta.appendChild(itemsEl);

        li.appendChild(main);
        li.appendChild(meta);
        storesList.appendChild(li);
      });
    }

    function updateClearButton() {
      if (!clearBtn) return;
      var hasValue = !!(queryInput.value.trim() || state.zip);
      clearBtn.hidden = !hasValue;
    }

    function clearLocation() {
      clearPendingInputTimers();
      state.storesFetchRequestId += 1;
      queryInput.value = '';
      state.query = '';
      state.zip = '';
      state.geocodedZip = '';
      state.locationLabel = '';
      state.lat = null;
      state.lng = null;
      state.isGeocoding = false;
      state.isFetchingStores = false;
      clearStorePreview();
      clearStoredLocation();
      syncWizzyAfterClear(state.radius);
      showStatus('', false);
      updateUi();
      queryInput.focus();
    }

    function updateUi() {
      syncRadiusControls();
      filterStores();
      var count = state.matched.length;
      var items = totalItems();
      var distancePending = isDistancePending();
      setTextContent(storeCountEl, count + ' store' + (count === 1 ? '' : 's'));
      setTextContent(itemCountEl, items.toLocaleString() + ' items');
      setTextContent(applyCountEl, items.toLocaleString());
      if (applyBtn) {
        applyBtn.disabled =
          state.isFetchingStores ||
          state.isGeocoding ||
          distancePending ||
          !state.zip ||
          state.zip.length !== 5;
      }

      if (state.isFetchingStores) {
        showStatus('Finding stores near you…', false);
      } else if (distancePending) {
        showStatus('Calculating distances…', false);
      } else if ((state.lat != null && state.locationLabel) || (state.zip && state.zip.length === 5)) {
        var locationText = state.locationLabel || state.zip;
        showStatus(
          '✓ ' + locationText + ' · ' + count + ' stores within ' + state.radius + ' mi',
          false
        );
      } else if (!state.isGeocoding) {
        showStatus('', false);
      }

      renderStoreList();
      updateClearButton();
    }

    function applyLocationFromGeocode(result) {
      if (!isValidCoord(result.lat, result.lng)) {
        state.lat = null;
        state.lng = null;
      } else {
        state.lat = result.lat;
        state.lng = result.lng;
      }
      state.locationLabel = result.label;
      state.query = result.query;
      if (result.zip) state.zip = result.zip;
      else state.zip = extractZipFromQuery(state.query);
      state.geocodedZip = isCompleteZipInput(state.query) ? state.zip : '';
      queryInput.value = state.query;
      safeUpdateUi();
      scheduleStoresFetch();
    }

    function syncZipFromInput() {
      var q = queryInput.value.trim();
      if (!q) {
        state.query = '';
        state.zip = '';
        state.geocodedZip = '';
        return '';
      }
      state.query = q;
      if (isPartialZipInput(q)) {
        state.zip = '';
        state.geocodedZip = '';
        return '';
      }
      var zip = extractZipFromQuery(q);
      if (zip && zip.length === ZIP_DIGITS) {
        if (state.zip !== zip) {
          state.lat = null;
          state.lng = null;
          state.geocodedZip = '';
        }
        state.zip = zip;
        if (!state.locationLabel || isCompleteZipInput(q)) state.locationLabel = zip;
      }
      return zip || state.zip;
    }

    function runZipGeocode() {
      var zip = state.zip || extractZipFromQuery(queryInput.value.trim());
      if (!zip || zip.length !== ZIP_DIGITS) return;
      if (!apiKey) return;
      if (state.geocodedZip === zip && isValidCoord(state.lat, state.lng)) {
        filterStores();
        updateUi();
        return;
      }

      state.isGeocoding = true;
      updateUi();
      geocodeQuery(zip, apiKey)
        .then(function (res) {
          state.isGeocoding = false;
          if (isValidCoord(res.lat, res.lng)) {
            state.lat = res.lat;
            state.lng = res.lng;
            state.geocodedZip = zip;
          } else {
            state.lat = null;
            state.lng = null;
            state.geocodedZip = '';
          }
          if (isCompleteZipInput(queryInput.value.trim())) {
            state.locationLabel = zip;
          }
          filterStores();
          updateUi();
        })
        .catch(function () {
          state.isGeocoding = false;
          state.lat = null;
          state.lng = null;
          state.geocodedZip = '';
          updateUi();
        });
    }

    function scheduleZipGeocode(immediate) {
      var zip = state.zip || extractZipFromQuery(queryInput.value.trim());
      if (!zip || zip.length !== ZIP_DIGITS || !apiKey) return;
      clearTimeout(state.geocodeTimer);
      if (immediate) {
        runZipGeocode();
      } else {
        state.geocodeTimer = setTimeout(runZipGeocode, INPUT_DEBOUNCE_MS);
      }
    }

    function runGeocode() {
      var q = queryInput.value.trim();
      if (!q) {
        state.lat = null;
        state.lng = null;
        state.locationLabel = '';
        state.geocodedZip = '';
        clearStorePreview();
        updateUi();
        return;
      }
      if (isPartialZipInput(q) || isCompleteZipInput(q)) return;
      if (!apiKey) {
        showStatus('Maps API key is not configured.', true);
        return;
      }
      state.isGeocoding = true;
      showStatus('Finding location…', false);
      geocodeQuery(q, apiKey)
        .then(function (res) {
          state.isGeocoding = false;
          try {
            applyLocationFromGeocode(res);
          } catch (err) {
            console.error('[ez-location-sheet] applyLocationFromGeocode failed', err);
            showStatus(
              'We couldn\u2019t load stores for that location. Try a zip code (78704) or city and state (Austin, TX).',
              true
            );
            safeUpdateUi();
          }
        })
        .catch(function (err) {
          state.isGeocoding = false;
          state.lat = null;
          state.lng = null;
          state.geocodedZip = '';
          showStatus(
            userFacingError(
              err,
              'We couldn\u2019t find that location. Try a zip code (78704) or city and state (Austin, TX).'
            ),
            true
          );
          safeUpdateUi();
        });
    }

    function scheduleGeocode() {
      var q = queryInput.value.trim();
      if (!q || isPartialZipInput(q) || isCompleteZipInput(q)) return;
      clearTimeout(state.geocodeTimer);
      state.geocodeTimer = setTimeout(function () {
        runThrottledApiCall(runGeocode);
      }, INPUT_DEBOUNCE_MS);
    }

    function handleQueryInput(immediate) {
      var q = queryInput.value.trim();
      syncZipFromInput();
      clearPendingInputTimers();

      if (!q) {
        state.locationLabel = '';
        state.lat = null;
        state.lng = null;
        state.geocodedZip = '';
        clearStorePreview();
        updateUi();
        return;
      }

      if (isPartialZipInput(q)) {
        state.zip = '';
        state.locationLabel = '';
        state.lat = null;
        state.lng = null;
        state.geocodedZip = '';
        clearStorePreview();
        updateUi();
        return;
      }

      var execute = function () {
        syncZipFromInput();
        var current = queryInput.value.trim();
        if (!current || isPartialZipInput(current)) return;

        if (hasCompleteZip(current)) {
          if (isCompleteZipInput(current)) {
            scheduleZipGeocode(immediate);
          } else if (apiKey) {
            if (immediate) runThrottledApiCall(runGeocode);
            else scheduleGeocode();
          }
          scheduleStoresFetch(immediate);
          return;
        }

        if (immediate) runThrottledApiCall(runGeocode);
        else scheduleGeocode();
      };

      if (immediate) {
        runThrottledApiCall(execute);
      } else {
        state.inputDebounceTimer = setTimeout(execute, INPUT_DEBOUNCE_MS);
      }
    }

    function getMatchedStoreIds() {
      filterStores();
      return state.matched.map(function (s) {
        return String(s.id);
      });
    }

    function applyToWizzy() {
      var ids = getMatchedStoreIds();
      localStorage.setItem(STORAGE_SOURCES, JSON.stringify(ids));
      savePreference({
        query: state.query,
        zip: state.zip || state.query,
        locationLabel: state.locationLabel,
        lat: isValidCoord(state.lat, state.lng) ? state.lat : null,
        lng: isValidCoord(state.lat, state.lng) ? state.lng : null,
        radius: state.radius,
      });

      document.dispatchEvent(
        new CustomEvent('wizzy:location-updated', {
          detail: {
            zip: state.zip,
            radius: state.radius,
            storeIds: ids,
            lat: isValidCoord(state.lat, state.lng) ? state.lat : null,
            lng: isValidCoord(state.lat, state.lng) ? state.lng : null,
            locationLabel: state.locationLabel || state.zip || state.query,
          },
        })
      );

      if (window.wizzyGeoCodeFilter) {
        try {
          if (typeof window.wizzyGeoCodeFilter.setCurrentZip === 'function') {
            window.wizzyGeoCodeFilter.setCurrentZip(state.zip || state.query, true);
          }
          if (typeof window.wizzyGeoCodeFilter.setCurrentRadius === 'function') {
            window.wizzyGeoCodeFilter.setCurrentRadius(state.radius);
          }
        } catch (e) {}
      }

      executeWizzyLocationFilter(ids);
    }

    window.EzLocationSheet = {
      open: function () {
        loadStoresFromDom();
        radiusInput = root.querySelector('[data-ez-location-radius]');
        radiusLabel = root.querySelector('[data-ez-location-radius-label]');
        var latestPref = loadPreference();
        if (latestPref && latestPref.radius != null && latestPref.radius !== '') {
          state.radius = normalizeRadius(latestPref.radius);
        } else {
          var storedRadius = parseInt(localStorage.getItem(STORAGE_RADIUS), 10);
          if (!isNaN(storedRadius)) state.radius = normalizeRadius(storedRadius);
        }
        syncRadiusControls();
        setOpen(true);
        if (state.zip && state.zip.length === 5) {
          scheduleStoresFetch();
          if (isCompleteZipInput(state.query || state.zip)) {
            scheduleZipGeocode(true);
          }
        }
      },
      close: function () {
        setOpen(false);
      },
      clear: clearLocation,
    };

    root.querySelectorAll('[data-ez-location-close]').forEach(function (el) {
      el.addEventListener('click', function () {
        setOpen(false);
      });
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && root.classList.contains('is-open')) setOpen(false);
    });

    queryInput.addEventListener('input', function () {
      handleQueryInput(false);
    });
    queryInput.addEventListener('change', function () {
      handleQueryInput(true);
    });

    if (clearBtn) {
      clearBtn.addEventListener('click', function () {
        clearLocation();
      });
    }

    radiusInput.addEventListener('input', function () {
      state.radius = normalizeRadius(radiusInput.value);
      if (hasCompleteZip(queryInput.value.trim()) || (state.zip && state.zip.length === ZIP_DIGITS)) {
        scheduleStoresFetch();
      }
      updateUi();
    });

    applyBtn.addEventListener('click', function () {
      var q = queryInput.value.trim();
      if (!q && state.lat == null) return;

      syncZipFromInput();

      function completeZipApply(stores) {
        state.isFetchingStores = false;
        state.apiStores = stores || [];
        filterStores();
        try {
          applyToWizzy();
        } catch (e) {
          console.warn('[ez-location-sheet] applyToWizzy failed', e);
        }
        setOpen(false);
        applyBtn.disabled = false;
      }

      function finishApply() {
        syncZipFromInput();
        var zipToSave = state.zip || extractZipFromQuery(state.query);
        if (!zipToSave || zipToSave.length !== 5) {
          showStatus('Please enter a valid 5-digit zip code.', true);
          return;
        }
        state.zip = zipToSave;
        try {
          localStorage.setItem(STORAGE_ZIP, zipToSave);
        } catch (e) {}
        localStorage.setItem(STORAGE_RADIUS, String(state.radius));

        if (state.apiStores.length && !state.isFetchingStores) {
          completeZipApply(state.apiStores);
          return;
        }

        state.isFetchingStores = true;
        applyBtn.disabled = true;
        showStatus('Applying location…', false);

        fetchGeocodeStores(state.zip, state.radius)
          .then(function (stores) {
            completeZipApply(stores);
          })
          .catch(function () {
            state.isFetchingStores = false;
            applyBtn.disabled = false;
            showStatus('Could not load stores for this location.', true);
            updateUi();
          });
      }

      if (state.lat == null) {
        if (!q) return;
        if (!apiKey) {
          showStatus('Maps API key is not configured.', true);
          return;
        }
        state.isGeocoding = true;
        showStatus('Finding location…', false);
        applyBtn.disabled = true;
        geocodeQuery(q, apiKey)
          .then(function (res) {
            state.isGeocoding = false;
            applyBtn.disabled = false;
            applyLocationFromGeocode(res);
            finishApply();
          })
          .catch(function (err) {
            state.isGeocoding = false;
            applyBtn.disabled = false;
            if (state.zip) {
              try {
                localStorage.setItem(STORAGE_ZIP, state.zip);
              } catch (e) {}
            }
            showStatus(
              userFacingError(
                err,
                'We couldn\u2019t find that location. Try a zip code (78704) or city and state (Austin, TX).'
              ),
              true
            );
            safeUpdateUi();
          });
        return;
      }

      finishApply();
    });

    geoBtn.addEventListener('click', function () {
      if (!navigator.geolocation) {
        showStatus('Location is not supported in this browser.', true);
        return;
      }
      if (!apiKey) {
        showStatus('Maps API key is not configured.', true);
        return;
      }
      geoBtn.disabled = true;
      showStatus('Getting your location…', false);
      navigator.geolocation.getCurrentPosition(
        function (pos) {
          findZipFromCoords(pos.coords.latitude, pos.coords.longitude, apiKey)
            .then(function (res) {
              geoBtn.disabled = false;
              state.zip = res.zip || '';
              state.geocodedZip = '';
              applyLocationFromGeocode(res);
            })
            .catch(function (err) {
              geoBtn.disabled = false;
              showStatus(
                userFacingError(err, 'We couldn\u2019t determine your location. Try entering a zip code instead.'),
                true
              );
            });
        },
        function () {
          geoBtn.disabled = false;
          showStatus('Location permission denied.', true);
        },
        { enableHighAccuracy: true, timeout: 12000 }
      );
    });

    if (pref && isValidCoord(state.lat, state.lng)) {
      updateUi();
      if (pref.zip && pref.zip.length === ZIP_DIGITS) {
        scheduleStoresFetch();
        if (isCompleteZipInput(pref.query || pref.zip)) {
          scheduleZipGeocode(true);
        }
      }
    } else if (pref && pref.query && pref.zip && pref.zip.length === ZIP_DIGITS && isCompleteZipInput(pref.query)) {
      scheduleStoresFetch();
      scheduleZipGeocode(true);
      updateUi();
    } else if (pref && pref.query && apiKey) {
      runGeocode();
    } else if (pref && pref.zip && pref.zip.length === ZIP_DIGITS) {
      scheduleStoresFetch();
      scheduleZipGeocode(true);
      updateUi();
    } else {
      updateUi();
    }
  }

  function boot() {
    installSafeExecuteWizzyFiltersFromURL();
    installSafeExecuteFilterApi();
    init();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
